import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

/**
 * Manages all server-side data for the collection / wishlist view:
 * fetching, optimistic CRUD updates, and bulk operations.
 *
 * @param {string}  slug         - URL username slug
 * @param {boolean} isWishlist   - true when viewing a wishlist, false for collection
 * @param {number|null} ownerUserId - numeric user_id of the authenticated owner (null if viewing another user)
 */
export function useCollectionData({ slug, isWishlist, ownerUserId }) {
    const [sets, setSets] = useState([]);
    const [profileData, setProfileData] = useState(null);
    const [trophies, setTrophies] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isPrivateProfile, setIsPrivateProfile] = useState(false);
    const [userExists, setUserExists] = useState(true);
    const [pendingUpdates, setPendingUpdates] = useState({});

    const endpoints = useMemo(() => ({
        fetch:          isWishlist ? 'get_user_wishlist.php'            : 'get_user_collection.php',
        remove:         isWishlist ? 'remove_set_from_wishlist.php'     : 'remove_set_from_collection.php',
        moveToCollection: 'move_wishlist_to_collection.php',
        updateQuantity: 'update_set_quantity.php',
        updateComplete: 'update_set_complete_count.php',
        updateSealed:   'update_set_sealed_count.php',
        bulkRemove:     'bulk_remove_from_collection.php',
        bulkOpen:       'bulk_open_collection_sets.php',
    }), [isWishlist]);

    // ── Fetch ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!slug) return;

        const fetchSets = async () => {
            setIsLoading(true);
            setUserExists(true);
            setIsPrivateProfile(false);

            try {
                const response = await axios.get(
                    `${API}/${endpoints.fetch}?username=${encodeURIComponent(slug)}`
                );
                if (response.data.error) {
                    if (response.data.error === 'User not found.') setUserExists(false);
                    else if (response.data.error === "This user's profile is private.") setIsPrivateProfile(true);
                    console.error('Error fetching collection/wishlist:', response.data.error);
                } else {
                    setSets(response.data.sets);
                    setProfileData(response.data.profile);
                    setTrophies(response.data.trophies || []);
                }
            } catch (err) {
                console.error('Error fetching collection/wishlist:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSets();
    }, [slug, endpoints.fetch]);

    // ── Generic optimistic field updater ────────────────────────────────────
    /**
     * @param {string}   set_num       - set identifier
     * @param {string}   pendingKey    - key within pendingUpdates[set_num] ('quantity'|'complete'|'sealed')
     * @param {Function} optimisticFn  - (set) => updatedSet  applied before the API call
     * @param {object}   payload       - body fields sent to the API (merged with user_id + set_num)
     * @param {string}   endpointKey   - key in the endpoints map
     * @param {Function} serverUpdateFn - (set, responseData) => updatedSet  applied on success
     */
    const updateSetField = async (set_num, pendingKey, optimisticFn, payload, endpointKey, serverUpdateFn) => {
        if (isWishlist) return Promise.reject(`Cannot update ${pendingKey} for wishlist items`);

        setPendingUpdates(prev => ({ ...prev, [set_num]: { ...prev[set_num], [pendingKey]: true } }));
        const originalSet = { ...sets.find(s => s.set_num === set_num) };

        setSets(prev => prev.map(s => s.set_num === set_num ? optimisticFn(s) : s));

        try {
            const response = await axios.post(
                `${API}/${endpoints[endpointKey]}`,
                { user_id: ownerUserId, set_num, ...payload },
                { withCredentials: true }
            );
            if (response.data.success) {
                setSets(prev => prev.map(s => s.set_num === set_num ? serverUpdateFn(s, response.data) : s));
                return Promise.resolve();
            }
            throw new Error(response.data.error || `Failed to update ${pendingKey}`);
        } catch (err) {
            console.error(`Error updating ${pendingKey}:`, err);
            setSets(prev => prev.map(s => s.set_num === set_num ? originalSet : s));
            return Promise.reject(err);
        } finally {
            setPendingUpdates(prev => ({ ...prev, [set_num]: { ...prev[set_num], [pendingKey]: false } }));
        }
    };

    // ── CRUD operations ──────────────────────────────────────────────────────
    const updateQuantity = (set_num, newQuantity) =>
        updateSetField(
            set_num, 'quantity',
            (s) => {
                const updated = { ...s, quantity: newQuantity };
                if (Number(s.complete) > newQuantity) updated.complete = newQuantity.toString();
                if (Number(s.sealed)   > newQuantity) updated.sealed   = newQuantity.toString();
                return updated;
            },
            { quantity: newQuantity },
            'updateQuantity',
            (s, data) => ({
                ...s,
                quantity: data.quantity.toString(),
                complete: data.complete_count.toString(),
                sealed:   data.sealed_count.toString(),
            })
        );

    const updateCompleteCount = (set_num, newCount) =>
        updateSetField(
            set_num, 'complete',
            (s) => ({ ...s, complete: newCount.toString() }),
            { complete_count: newCount },
            'updateComplete',
            (s, data) => ({ ...s, complete: data.complete_count.toString() })
        );

    const updateSealedCount = (set_num, newCount) =>
        updateSetField(
            set_num, 'sealed',
            (s) => ({ ...s, sealed: newCount.toString() }),
            { sealed_count: newCount },
            'updateSealed',
            (s, data) => ({ ...s, sealed: data.sealed_count.toString() })
        );

    const removeSet = async (set_num) => {
        try {
            const response = await axios.post(
                `${API}/${endpoints.remove}`,
                { user_id: ownerUserId, set_num },
                { withCredentials: true }
            );
            if (response.data.success) {
                setSets(prev => prev.filter(s => s.set_num !== set_num));
            }
        } catch (err) {
            console.error('Error removing set:', err);
        }
    };

    const moveToCollection = async (set_num) => {
        if (!isWishlist) return;
        try {
            const response = await axios.post(
                `${API}/${endpoints.moveToCollection}`,
                { user_id: ownerUserId, set_num },
                { withCredentials: true }
            );
            if (response.data.success) {
                setSets(prev => prev.filter(s => s.set_num !== set_num));
            }
        } catch (err) {
            console.error('Error moving set to collection:', err);
        }
    };

    const bulkRemove = async (setNums) => {
        const response = await axios.post(
            `${API}/${endpoints.bulkRemove}`,
            { user_id: ownerUserId, set_nums: setNums },
            { withCredentials: true }
        );
        if (response.data.success) {
            const removed = new Set(setNums);
            setSets(prev => prev.filter(s => !removed.has(s.set_num)));
        }
    };

    const bulkOpen = async (setNums) => {
        const response = await axios.post(
            `${API}/${endpoints.bulkOpen}`,
            { user_id: ownerUserId, set_nums: setNums },
            { withCredentials: true }
        );
        if (response.data.success) {
            const opened = new Set(setNums);
            setSets(prev => prev.map(s => opened.has(s.set_num) ? { ...s, sealed: '0' } : s));
        }
    };

    return {
        sets,
        setSets,
        profileData,
        trophies,
        isLoading,
        isPrivateProfile,
        userExists,
        pendingUpdates,
        updateQuantity,
        updateCompleteCount,
        updateSealedCount,
        removeSet,
        moveToCollection,
        bulkRemove,
        bulkOpen,
    };
}
